import { NgIf } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService, HttpErrorResponse, UsernameOrEmailCheckResponse } from '../../services/auth.service';
import { SharedModule } from '../../shared/modules/shared.module';
import * as Utils from '../../utils/email.util';
import { LoadingComponent } from '../loading/loading.component';
import { isEmail } from '../../utils/email.util';
import { AuthkitService } from '../../services/authKit.service';
import { showSnackBar } from '../../utils/snackBar.ulti';
import { PAGE_ROUTES } from '../../constants';
import { catchError, throwError } from 'rxjs';
import { extractErrors } from '../../utils/extracErrors.util';

@Component({
  selector: 'app-welcome',
  imports: [LoadingComponent, MatFormField, MatInput, NgIf, ReactiveFormsModule, TranslatePipe, MatLabel, SharedModule],
  templateUrl: './welcome.component.html',
  standalone: true,
  styleUrl: './welcome.component.scss'
})
export class WelcomeComponent implements OnInit {
  protected isLoading = false;
  protected form: FormGroup;
  protected get userNameOrEmail() {
    return this.form.get('usernameOrEmail');
  }
  private readonly _notMigratedEmailNotiMessage = 'This account hasn’t been migrated. Log in to start the migration process';
  private readonly _invalidEmailNotiMessage = 'Email is invalid';
  private _snackBar = inject(MatSnackBar);

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _formBuilder: FormBuilder,
    private _authService: AuthService,
    private _authKitService: AuthkitService,
    private _translate: TranslateService
  ) {
    this.form = this._formBuilder.group({
      usernameOrEmail: ['', [Validators.required, Validators.minLength(1)]]
    });
  }

  ngOnInit(): void {
    this._route.queryParamMap.subscribe((params) => {
      let email = decodeURIComponent(params.get('email') || '');
      email = email.replaceAll(' ', '+');

      if (email && !isEmail(email)) {
        this.isLoading = false;
        showSnackBar({ type: 'error', message: this._invalidEmailNotiMessage, snackBar: this._snackBar, translate: this._translate });

        return;
      }

      this.form.patchValue({
        usernameOrEmail: email
      });
    });
  }

  onSubmit = () => {
    if (this.form.valid) {
      const usernameOrEmail = this.form.value.usernameOrEmail;

      if (Utils.isEmail(usernameOrEmail)) {
        this._handleCheckMigrated(usernameOrEmail);
      } else {
        this._onEnterUsername(usernameOrEmail);
      }
    }
  };

  private _handleCheckMigrated(loginHint: string): void {
    this.isLoading = true;

    this._authService
      .checkUsernameOrEmail(loginHint)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this._extractErrors(error);

          return throwError(() => error);
        })
      )
      .subscribe(async (result: UsernameOrEmailCheckResponse) => {
        const { userExists, isUserMigrated, username } = result;

        if (!userExists) {
          this._onUserNotExisted(loginHint);

          return;
        }

        if (isUserMigrated) {
          this._onUserMigrated(loginHint);

          return;
        }

        this._onUserNotMigrated(username);
      });
  }

  private _onEnterUsername(username: string) {
    this._router.navigate([PAGE_ROUTES.LOGIN], { queryParams: { email: username } });
  }

  private _onUserNotExisted(loginHint: string) {
    this._router.navigate([PAGE_ROUTES.NOT_FOUND], {
      state: { returnTo: window.location.pathname, queryParams: { email: loginHint } }
    });

    this.isLoading = false;
  }

  private async _onUserMigrated(loginHint: string): Promise<void> {
    // Redirect to workos sign-in
    await this._authKitService.init({ redirectUri: window.location.origin });
    await this._authKitService.signIn({ loginHint });
  }

  private _onUserNotMigrated(username: string): void {
    showSnackBar({ type: 'error', message: this._notMigratedEmailNotiMessage, snackBar: this._snackBar, translate: this._translate });
    this._router.navigate([PAGE_ROUTES.LOGIN], { queryParams: { email: username } });
  }

  private _extractErrors(apiResponse: HttpErrorResponse): void {
    this.isLoading = false;
    const { status } = apiResponse ?? {};

    extractErrors({ status, snackBar: this._snackBar, translate: this._translate });
  }
}
